import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  useMediaQuery,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBackIos,
  ArrowForwardIos,
  TrendingUp,
  AccountBalance,
  Copyright,
  Language,
  AccountBalanceWallet,
  Download,
} from "@mui/icons-material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import MarqueService from "../../api/services/MarqueService";
import BrevetService from "../../api/services/BrevetService";
import NomDomaineService from "../../api/services/NomDomaineService";
import ContratService from "../../api/services/ContratService";
import SocieteService from "../../api/services/SocieteService";
import { styled, alpha } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  padding: '24px',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: `linear-gradient(90deg, 
    #2a5fa9 0%, 
    #3a7fb5 20%,
    #4a9fc0 40%, 
    #5ab8a0 60%,
    #70bd8d 80%, 
    #8fc68b 100%)`,
  '& .MuiTableCell-head': {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: 'none',
    padding: '16px 8px',
    whiteSpace: 'nowrap',
    minWidth: '80px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  padding: '8px 4px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  fontSize: '0.8rem',
  '&:nth-of-type(even)': {
    backgroundColor: alpha(theme.palette.action.hover, 0.02),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    cursor: 'pointer',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    padding: '6px 2px',
  },
}));

const YearNavigator = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: '50px',
  padding: '8px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.2),
    transform: 'scale(1.1)',
  },
  '&:disabled': {
    background: alpha(theme.palette.action.disabled, 0.1),
    color: theme.palette.action.disabled,
  },
  transition: 'all 0.2s ease',
}));

const getCostChipStyles = (theme, costType) => ({
  backgroundColor: "white",
  color: "black",
  border: "1px solid #ccc",
  fontWeight: 600,
  fontSize: "0.75rem",
  borderRadius: "8px",
  margin: "2px",
  "&:hover": {
    backgroundColor: "white",
    border: "1px solid #f5f1f1ff",
  },
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#000000',
  fontWeight: 800,
  fontSize: '1.5rem',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
}));
// calendrier 

const Calendar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("user_role");
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isAdmin = userRole === "admin";

  const currentYearActual = new Date().getFullYear();
  const [currentYear, setCurrentYear] = useState(currentYearActual);
  const [societes, setSocietes] = useState([]);
  const [societeFilter, setSocieteFilter] = useState("");
  const [marquesEvents, setMarquesEvents] = useState([]);
  const [brevetsEvents, setBrevetsEvents] = useState([]);
  const [domainesEvents, setDomainesEvents] = useState([]);
  const [contratsData, setContratsData] = useState({
    monthlyPaymentsByCode: [],
    totauxGeneraux: { total_ht: 0, total_tva: 0 },
    monthlyTotals: Array(12).fill(0),
    monthlyTvaTotals: Array(12).fill(0),
    contratsEvents: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const months = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
  ];

  // Charger les sociétés (admin seulement)
  useEffect(() => {
    if (!isAdmin) return;
    const loadSocietes = async () => {
      try {
        const data = await SocieteService.getAll(token);
        setSocietes(data);
      } catch (err) {
        console.error("Erreur chargement sociétés", err);
      }
    };
    loadSocietes();
  }, [token, isAdmin]);

  // Charger les sociétés pour ADMIN et GESTIONNAIRE
  useEffect(() => {
    const loadSocietes = async () => {
      try {
        const data = await SocieteService.getAll(token, userRole);
        setSocietes(data);
      } catch (err) {
        console.error("Erreur chargement sociétés", err);
      }
    };
    loadSocietes();
  }, [token, userRole]);

  // Filtre par société
  const filterBySociete = (events) => {
    if (!societeFilter) return events;
    return events.filter(e => {
      if (e.societe_id === null || e.societe_id === undefined) {
        return societes.length === 1 && String(societes[0].id) === String(societeFilter);
      }
      return String(e.societe_id) === String(societeFilter);
    });
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateCostTableCSV = (events, title) => {
    const groupedByCompte = {};
    events.forEach((event) => {
      const { compte_comptable, month, cost, currency } = event;
      if (!groupedByCompte[compte_comptable]) {
        groupedByCompte[compte_comptable] = Array(12).fill(null).map(() => ({
          cost: 0,
          currency: currency || "EUR",
        }));
      }
      groupedByCompte[compte_comptable][month].cost += Number(cost) || 0;
    });

    const headers = ["Compte Comptable", ...months];
    const data = Object.keys(groupedByCompte).map((compte) => {
      const row = [compte];
      groupedByCompte[compte].forEach((event) => {
        row.push(event.cost > 0 ? `${event.cost.toFixed(2)} ${event.currency}` : "-");
      });
      return row;
    });

    return "\uFEFF" + [
      headers.map((h) => `"${h}"`).join(";"),
      ...data.map((row) => row.map((cell) => `"${cell.toString().replace(/"/g, '""').replace(/\n/g, " ").replace(/\r/g, " ")}"`).join(";")),
    ].join("\n");
  };

  const generateContratTableCSV = (contratsData, title) => {
    const headers = ["Compte Comptable", ...months.flatMap((month) => [`${month} HT`, `${month} TVA`])];
    const groupedByCompte = {};

    contratsData.monthlyPaymentsByCode.forEach((event) => {
      const year = parseInt(event.month.split("-")[0]);
      if (year !== currentYear) return;
      const { compte_comptable, month, total_loyer_ht, total_tva, currency } = event;
      const groupKey = `${compte_comptable} (${currency || "-"})`;
      if (!groupedByCompte[groupKey]) {
        groupedByCompte[groupKey] = Array(12).fill(null).map(() => ({ ht: null, tva: null, currency: currency || "-" }));
      }
      const monthIndex = parseInt(month.split("-")[1]) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        groupedByCompte[groupKey][monthIndex] = {
          ht: Number(total_loyer_ht).toFixed(2),
          tva: Number(total_tva).toFixed(2),
          currency: currency || "-",
        };
      }
    });

    const data = Object.keys(groupedByCompte).map((compte) => {
      const row = [compte];
      groupedByCompte[compte].forEach((values) => {
        row.push(values.ht === null ? "-" : `${values.ht} ${values.currency}`);
        row.push(values.tva === null ? "-" : `${values.tva} ${values.currency}`);
      });
      return row;
    });

    return "\uFEFF" + [
      headers.map((h) => `"${h}"`).join(";"),
      ...data.map((row) => row.map((cell) => `"${cell.toString().replace(/"/g, '""').replace(/\n/g, " ").replace(/\r/g, " ")}"`).join(";")),
    ].join("\n");
  };

  const generateTotalTableCSV = (marquesEvents, brevetsEvents, domainesEvents, contratsData) => {
    const totals = Array(12).fill(0);
    marquesEvents.forEach((e) => { totals[e.month] += Number(e.cost) || 0; });
    brevetsEvents.forEach((e) => { totals[e.month] += Number(e.cost) || 0; });
    domainesEvents.forEach((e) => { totals[e.month] += Number(e.cost) || 0; });
    contratsData.monthlyTotals.forEach((total, idx) => { totals[idx] += Number(total) || 0; });

    return "\uFEFF" + [
      months.map((h) => `"${h}"`).join(";"),
      totals.map((cell) => `"${Number(cell).toFixed(2).replace(/"/g, '""')}"`).join(";"),
    ].join("\n");
  };


  const getCompteCode = (compte) => {
    if (!compte) return "-";
    if (typeof compte === "object") {
      return compte.code || "-";
    }
    if (typeof compte === "string") {
      const idx = compte.indexOf(" - ");
      return idx !== -1 ? compte.substring(0, idx).trim() : compte.trim();
    }
    return "-";
  };

  const fetchCalendarData = useCallback(async () => {
    if (!token) {
      setError("Aucun jeton d'authentification trouvé.");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const [marquesData, brevetsData, domainesData, monthlyPaymentsResponse] = await Promise.all([
        MarqueService.getMarques(userRole, token).catch(() => ({ marques: [] })),
        BrevetService.getBrevet(userRole, token).catch(() => ({ brevets: [] })),
        NomDomaineService.getAllNomsDomaine(userRole).catch(() => ({ monthlyRenouvellements: [] })),
        ContratService.getMonthlyPayments(userRole, token, currentYear).catch(() => ({
          contrats_events: [],
          monthly_payments_by_accounting_code: [],
          totaux_generaux: { total_ht: 0, total_tva: 0 },
        })),
      ]);

      const marquesArray = Array.isArray(marquesData)
        ? marquesData
        : (marquesData?.marques || marquesData?.resultats || []);
      const marquesEvents = [];
      const brevetsEvents = [];

      const contratsEvents = (monthlyPaymentsResponse.contrats_events || [])
        .filter((event) => new Date(event.date_full).getFullYear() === currentYear)
        .map((event) => ({
          month: event.month,
          cost: Number(event.cost) || 0,
          costTVA: Number(event.cost_tva) || 0,
          compte_comptable: getCompteCode(event.compte_comptable),
          currency: event.currency || "EUR",
          dateFull: event.date_full,
          societe_id: event.societe_id,
          societe_nom: event.societe_nom,
        }));

      // Domaines
      const groupedDomaines = {};
      (domainesData.monthlyRenouvellements || []).forEach((domaine) => {
        const year = parseInt(domaine.mois.split("-")[0]);
        if (year === currentYear) {
          const monthIndex = parseInt(domaine.mois.split("-")[1]) - 1;
          if (monthIndex >= 0 && monthIndex < 12) {
            const key = `${domaine.compte_comptable}-${monthIndex}-${domaine.societe_id || ""}`;
            if (!groupedDomaines[key]) {
              groupedDomaines[key] = {
                month: monthIndex,
                cost: 0,
                dateFull: domaine.date_renouvellement || domaine.mois + "-01",
                year: year,
                currency: "EUR",
                compte_comptable: getCompteCode(domaine.compte_comptable),
                societe_id: domaine.societe_id ?? domaine.id_societe ?? domaine.societe ?? null,
                societe_nom: domaine.societe_nom,
              };
            }
            groupedDomaines[key].cost += Number(domaine.total_renouvellement_ht) || 0;
          }
        }
      });
      const domainesEvents = Object.values(groupedDomaines);
      console.log("domainesEvents RAW:", domainesEvents);
      console.log("societeFilter value:", societeFilter);
      // Marques
      marquesArray.forEach((marque) => {
        if (marque.date_depot && marque.date_de_renouvellement && marque.cout) {
          const depotDate = new Date(marque.date_depot);
          const renewDate = new Date(marque.date_de_renouvellement);


          const compteComptableLabel = getCompteCode(marque.compte_comptable);

          if (!isNaN(depotDate.getTime()) && !isNaN(renewDate.getTime())) {
            if (depotDate.getFullYear() === currentYear) {
              marquesEvents.push({
                month: depotDate.getMonth(),
                cost: Number(marque.cout) || 0,
                dateFull: depotDate.toISOString().split("T")[0],
                year: depotDate.getFullYear(),
                currency: marque.devise || "EUR",
                compte_comptable: compteComptableLabel,
                societe_id: marque.societe_id,
                societe_nom: marque.societe_nom,
              });
            }
            if (renewDate.getFullYear() === currentYear) {
              marquesEvents.push({
                month: renewDate.getMonth(),
                cost: Number(marque.cout) || 0,
                dateFull: renewDate.toISOString().split("T")[0],
                year: renewDate.getFullYear(),
                currency: marque.devise || "EUR",
                compte_comptable: compteComptableLabel,
                societe_id: marque.societe_id,
                societe_nom: marque.societe_nom,
              });
            }
          }
        }
      });

      // Brevets
      brevetsData.brevets.forEach((brevet) => {
        if (brevet.date_depot && brevet.date_expiration && brevet.budget_total) {
          const depotDate = new Date(brevet.date_depot);
          const expirationDate = new Date(brevet.date_expiration);

          let currency = "EUR";
          if (brevet.annuites_details_calculated && brevet.annuites_details_calculated.length > 0) {
            currency = brevet.annuites_details_calculated[0].devise || "EUR";
          } else if (brevet.monthly_budgets) {
            const firstMonth = Object.values(brevet.monthly_budgets)[0];
            if (firstMonth && firstMonth[0] && firstMonth[0].devise) {
              currency = firstMonth[0].devise;
            }
          }

          if (!isNaN(depotDate.getTime()) && !isNaN(expirationDate.getTime())) {
            if (depotDate.getFullYear() === currentYear) {
              brevetsEvents.push({
                month: depotDate.getMonth(),
                cost: Number(brevet.budget_total) || 0,
                dateFull: depotDate.toISOString().split("T")[0],
                year: depotDate.getFullYear(),
                currency: currency,
                compte_comptable: getCompteCode(brevet.compte_comptable),
                societe_id: brevet.societe_id,
                societe_nom: brevet.societe_nom,
              });
            }
            if (expirationDate.getFullYear() === currentYear) {
              brevetsEvents.push({
                month: expirationDate.getMonth(),
                cost: Number(brevet.budget_total) || 0,
                dateFull: expirationDate.toISOString().split("T")[0],
                year: expirationDate.getFullYear(),
                currency: currency,
                compte_comptable: getCompteCode(brevet.compte_comptable),
                societe_id: brevet.societe_id,
                societe_nom: brevet.societe_nom,
              });
            }
          }
        }
      });

      const monthlyPaymentsByCode = (monthlyPaymentsResponse.monthly_payments_by_accounting_code || []).map((e) => ({
        ...e,
        compte_comptable: getCompteCode(e.compte_comptable),
        societe_id: e.societe_id,
        societe_nom: e.societe_nom,
      }));

      const totauxGeneraux = monthlyPaymentsResponse.totaux_generaux || { total_ht: 0, total_tva: 0 };

      const monthlyTotals = Array(12).fill(0);
      const monthlyTvaTotals = Array(12).fill(0);
      contratsEvents.forEach((event) => {
        monthlyTotals[event.month] += Number(event.cost) || 0;
        monthlyTvaTotals[event.month] += Number(event.costTVA) || 0;
      });

      setMarquesEvents(marquesEvents);
      setBrevetsEvents(brevetsEvents);
      setDomainesEvents(domainesEvents);
      setContratsData({ monthlyPaymentsByCode, totauxGeneraux, monthlyTotals, monthlyTvaTotals, contratsEvents });
    } catch (err) {
      console.error("Error fetching calendar data:", err);
      setError("Impossible de charger les données: " + err.message);
      setMarquesEvents([]);
      setBrevetsEvents([]);
      setDomainesEvents([]);
      setContratsData({
        monthlyPaymentsByCode: [],
        totauxGeneraux: { total_ht: 0, total_tva: 0 },
        monthlyTotals: Array(12).fill(0),
        monthlyTvaTotals: Array(12).fill(0),
        contratsEvents: [],
      });
    } finally {
      setLoading(false);
    }
  }, [currentYear, token, userRole]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Données filtrées
  const filteredMarques = filterBySociete(marquesEvents);
  const filteredBrevets = filterBySociete(brevetsEvents);
  const filteredDomaines = filterBySociete(domainesEvents);
  console.log("Filtered Marques:", filteredMarques);
  console.log("Filtered Brevets:", filteredBrevets);
  console.log("Filtered Domaines:", filteredDomaines);
  const filteredMonthlyPayments = filterBySociete(contratsData.monthlyPaymentsByCode);
  const filteredContratsEvents = filterBySociete(contratsData.contratsEvents);

  const filteredMonthlyTotals = (() => {
    const totals = Array(12).fill(0);
    filteredContratsEvents.forEach(e => { totals[e.month] += Number(e.cost) || 0; });
    return totals;
  })();

  const filteredContratsData = {
    ...contratsData,
    monthlyPaymentsByCode: filteredMonthlyPayments,
    contratsEvents: filteredContratsEvents,
    monthlyTotals: filteredMonthlyTotals,
  };

  const handlePreviousYear = () => setCurrentYear(currentYear - 1);
  const handleNextYear = () => setCurrentYear(currentYear + 1);

  const renderCostTable = (title, events, icon, costType) => {
    const groupedByCompte = {};
    events.forEach((event) => {
      const { compte_comptable, month, cost, currency, dateFull } = event;
      if (!groupedByCompte[compte_comptable]) {
        groupedByCompte[compte_comptable] = Array(12).fill(null).map(() => ({
          cost: 0, currency: currency || "EUR", dates: [],
        }));
      }
      groupedByCompte[compte_comptable][month].cost += Number(cost) || 0;
      groupedByCompte[compte_comptable][month].dates.push(dateFull);
    });

    return (
      <StyledCard sx={{ mb: 4, width: '100%', overflowX: 'auto' }}>
        <CardContent sx={{ p: isMobile ? 1 : 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <SectionTitle sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
              {icon}{title}
            </SectionTitle>
            <StyledIconButton
              onClick={() => downloadCSV(generateCostTableCSV(events, title), `${title}_${currentYear}.csv`)}
              title="Télécharger en CSV"
              size={isMobile ? "small" : "medium"}
            >
              <Download fontSize="small" />
            </StyledIconButton>
          </Box>
          <Box sx={{ overflowX: 'auto', width: '100%', mt: 2 }}>
            <Table sx={{ minWidth: 800 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120 }}>Compte Comptable</TableCell>
                  {months.map((month) => (
                    <TableCell key={month} align="center" sx={{ minWidth: 80 }}>
                      {isMobile ? month.substring(0, 3) : month}
                    </TableCell>
                  ))}
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {Object.keys(groupedByCompte).length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={13} align="center">
                      <Typography variant="body2" color="textSecondary">
                        Aucune donnée disponible pour {currentYear}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  Object.keys(groupedByCompte).map((compte) => (
                    <TableRow key={compte}>
                      <StyledTableCell sx={{ minWidth: 120, fontWeight: 500 }}>{compte}</StyledTableCell>
                      {groupedByCompte[compte].map((event, idx) => (
                        <StyledTableCell key={`${compte}-${idx}`} align="center">
                          {event.cost === 0 ? (
                            <Typography variant="body2" color="textSecondary">-</Typography>
                          ) : (
                            <Chip
                              label={`${event.cost.toFixed(2)} ${event.currency}`}
                              size="small"
                              sx={getCostChipStyles(theme, costType)}
                            />
                          )}
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  const renderContratTable = (title, contratsData) => {
    const groupedByCompte = {};
    contratsData.monthlyPaymentsByCode.forEach((event) => {
      const year = parseInt(event.month.split("-")[0]);
      if (year !== currentYear) return;
      const { compte_comptable, month, total_loyer_ht, total_tva, currency } = event;
      const groupKey = `${compte_comptable} (${currency || "EUR"})`;
      if (!groupedByCompte[groupKey]) {
        groupedByCompte[groupKey] = Array(12).fill(null).map(() => ({ ht: null, tva: null, currency: currency || "EUR" }));
      }
      const monthIndex = parseInt(month.split("-")[1]) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        groupedByCompte[groupKey][monthIndex] = {
          ht: Number(total_loyer_ht).toFixed(2),
          tva: Number(total_tva).toFixed(2),
          currency: currency || "EUR",
        };
      }
    });

    return (
      <StyledCard sx={{ mb: 4, width: '100%', overflowX: 'auto' }}>
        <CardContent sx={{ p: isMobile ? 1 : 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <SectionTitle sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
              <AccountBalanceWallet sx={{ color: '#2a5fa9' }} />{title}
            </SectionTitle>
            <StyledIconButton
              onClick={() => downloadCSV(generateContratTableCSV(contratsData, title), `${title}_${currentYear}.csv`)}
              title="Télécharger en CSV"
              size={isMobile ? "small" : "medium"}
            >
              <Download fontSize="small" />
            </StyledIconButton>
          </Box>
          <Box sx={{ overflowX: 'auto', width: '100%', mt: 2 }}>
            <Table sx={{ minWidth: 1400 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 150 }}>Compte Comptable</TableCell>
                  {months.map((month) => (
                    <TableCell key={month} align="center" colSpan={2} sx={{ minWidth: 100 }}>
                      {isMobile ? month.substring(0, 3) : month}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  {months.map((month) => (
                    <>
                      <TableCell key={`${month}-ht`} align="center" sx={{ fontSize: '0.7rem' }}>HT</TableCell>
                      <TableCell key={`${month}-tva`} align="center" sx={{ fontSize: '0.7rem' }}>TVA</TableCell>
                    </>
                  ))}
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {Object.keys(groupedByCompte).length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={25} align="center">
                      <Typography variant="body2" color="textSecondary">
                        Aucune donnée disponible pour {currentYear}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  Object.keys(groupedByCompte).map((compte) => (
                    <TableRow key={compte}>
                      <StyledTableCell sx={{ minWidth: 150, fontWeight: 500 }}>{compte}</StyledTableCell>
                      {groupedByCompte[compte].map((values, monthIdx) => (
                        <>
                          <StyledTableCell key={`${compte}-${monthIdx}-ht`} align="center">
                            {values === null || values.ht === null ? (
                              <Typography variant="body2" color="textSecondary">-</Typography>
                            ) : (
                              <Chip label={`${values.ht} ${values.currency}`} size="small" sx={getCostChipStyles(theme, "contrat")} />
                            )}
                          </StyledTableCell>
                          <StyledTableCell key={`${compte}-${monthIdx}-tva`} align="center">
                            {values === null || values.tva === null ? (
                              <Typography variant="body2" color="textSecondary">-</Typography>
                            ) : (
                              <Chip label={`${values.tva} ${values.currency}`} size="small" sx={getCostChipStyles(theme, "contrat")} />
                            )}
                          </StyledTableCell>
                        </>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  const renderTotalTable = (title, marquesEvents, brevetsEvents, domainesEvents, contratsData) => {
    const totals = Array(12).fill(0);
    marquesEvents.forEach((e) => { totals[e.month] += Number(e.cost) || 0; });
    brevetsEvents.forEach((e) => { totals[e.month] += Number(e.cost) || 0; });
    domainesEvents.forEach((e) => { totals[e.month] += Number(e.cost) || 0; });
    contratsData.monthlyTotals.forEach((total, idx) => { totals[idx] += Number(total) || 0; });

    const yearTotal = totals.reduce((sum, total) => sum + total, 0);

    return (
      <StyledCard sx={{ width: '100%', overflowX: 'auto' }}>
        <CardContent sx={{ p: isMobile ? 1 : 3 }}>
          <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={3} gap={2}>
            <SectionTitle sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
              <TrendingUp />{title}
            </SectionTitle>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                label={`Total Année: ${Number(yearTotal).toFixed(2)} EUR`}
                sx={getCostChipStyles(theme, "total")}
                size={isMobile ? "small" : "large"}
              />
              <StyledIconButton
                onClick={() => downloadCSV(generateTotalTableCSV(marquesEvents, brevetsEvents, domainesEvents, contratsData), `${title}_${currentYear}.csv`)}
                title="Télécharger en CSV"
                size={isMobile ? "small" : "medium"}
              >
                <Download fontSize="small" />
              </StyledIconButton>
            </Box>
          </Box>
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ minWidth: 800 }}>
              <StyledTableHead>
                <TableRow>
                  {months.map((month) => (
                    <TableCell key={month} align="center" sx={{ minWidth: 80 }}>
                      {isMobile ? month.substring(0, 3) : month}
                    </TableCell>
                  ))}
                </TableRow>
              </StyledTableHead>
              <TableBody>
                <TableRow>
                  {totals.map((total, idx) => (
                    <StyledTableCell key={idx} align="center">
                      {Number(total).toFixed(2) === "0.00" ? (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      ) : (
                        <Chip label={`${Number(total).toFixed(2)} EUR`} sx={getCostChipStyles(theme, "total")} size="small" />
                      )}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  return (
    <Box sx={{ m: { xs: 1, sm: 2, md: 3 } }}>
      <Header title="Calendrier des Coûts" subtitle="Visualisation des coûts mensuels" />

      {/* Navigateur année + filtre société dans le même pill */}
      <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
        <YearNavigator elevation={0}>
          {/* Navigation année */}
          <StyledIconButton
            onClick={handlePreviousYear}
            disabled={currentYear <= currentYearActual}
            size="small"
          >
            <ArrowBackIos fontSize="small" />
          </StyledIconButton>
          <Typography
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              minWidth: "120px",
              textAlign: "center",
              fontSize: { xs: '1.1rem', sm: '1.3rem' },
            }}
          >
            {currentYear}
          </Typography>
          <StyledIconButton
            onClick={handleNextYear}
            disabled={currentYear >= currentYearActual + 1}
            size="small"
          >
            <ArrowForwardIos fontSize="small" />
          </StyledIconButton>

          {/* Séparateur vertical — visible uniquement pour l'admin */}
          <Box
            sx={{
              width: '1px',
              height: '28px',
              backgroundColor: alpha(theme.palette.divider, 0.4),
              mx: 1,
            }}
          />

          <Select
            value={societeFilter}
            onChange={(e) => setSocieteFilter(e.target.value)}
            displayEmpty
            size="small"
            sx={{
              minWidth: 180,
              borderRadius: "50px",
              background: 'transparent',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: theme.palette.text.primary,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': { py: '4px', px: '8px' },
              '&:hover': { background: alpha(theme.palette.primary.main, 0.05) },
            }}
          >
            <MenuItem value="">Toutes les sociétés</MenuItem>
            {societes.map((soc) => (
              <MenuItem key={soc.id} value={soc.id}>{soc.nom}</MenuItem>
            ))}
          </Select>
        </YearNavigator>
      </Box>

      {loading ? (
        <StyledCard>
          <CardContent>
            <Box textAlign="center" py={6}>
              <CircularProgress color="primary" />
              <Typography variant="h6" sx={{ mt: 2 }}>Chargement des données...</Typography>
            </Box>
          </CardContent>
        </StyledCard>
      ) : error ? (
        <StyledCard>
          <CardContent>
            <Box textAlign="center" py={6}>
              <Typography variant="h6" color="error" sx={{ mb: 2 }}>Erreur de chargement</Typography>
              <Typography variant="body1" color="error">{error}</Typography>
            </Box>
          </CardContent>
        </StyledCard>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderCostTable("Marques", filteredMarques, <Copyright sx={{ color: '#2a5fa9' }} />, "marque")}
          {renderCostTable("Brevets", filteredBrevets, <AccountBalance sx={{ color: '#2a5fa9' }} />, "brevet")}
          {renderCostTable("Noms de Domaine", filteredDomaines, <Language sx={{ color: '#2a5fa9' }} />, "domaine")}
          {renderContratTable("Contrats", filteredContratsData)}
          <Divider sx={{ my: 2 }} />
          {renderTotalTable(
            "Récapitulatif des Coûts",
            filteredMarques,
            filteredBrevets,
            filteredDomaines,
            filteredContratsData
          )}
        </Box>
      )}
    </Box>
  );
};

export default Calendar;